import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  UiSliderNewSlide,
  UiSliderNew,
  UiButton,
  UiIcon,
  UiText,
} from '@frontend/znanie-uikit';
import dayjs from 'dayjs';
import SwiperClass from 'swiper';
import { LinkArrow } from 'components/common/LinkArrow';
import Cover from 'components/common/Cover';

import LayoutConfines from 'components/layout/LayoutConfines';

import { AnalyticsEvent, trackAnalyticsData } from 'utils/analytics';
import { shuffleAndSliceArray } from 'utils/helpers';

import { SpeakerStatusEnum, SpeakerType } from 'graphql/types';

import * as C from './SpeakersSlider.conts';

import css from './SpeakersOnMain.module.scss';

interface IProps {
  speakers: SpeakerType[];
}

type SpeakersOnMainCache = {
  dayOfYear: number;
  shuffledSpeakers: SpeakerType[];
};

export default function SpeakersOnMain({ speakers }: IProps) {
  const sliderRef = useRef<SwiperClass>();

  const router = useRouter();
  const [shuffledSpeakers, setShuffledSpeakers] =
    useState<SpeakerType[]>(speakers);
  const cachedKey: string = SpeakersOnMain.name;

  const getSpeakersCache = (key: string): SpeakersOnMainCache =>
    JSON.parse(localStorage.getItem(key) || '') as SpeakersOnMainCache;

  const prepareSpeakersCache = (
    dayOfYear: number,
    speakers: SpeakerType[]
  ): SpeakersOnMainCache => ({
    dayOfYear,
    shuffledSpeakers: shuffleAndSliceArray<SpeakerType>(
      speakers
    ) as SpeakerType[],
  });

  const saveSpeakersToCache = (
    key: string,
    payload: SpeakersOnMainCache
  ): void => localStorage.setItem(cachedKey, JSON.stringify(payload));

  useEffect(() => {
    //Кеш спикеров зависит от данной переменной, если её изменять, сортировка спикеров будет обновляться
    const currentDayOfYear: number = dayjs().dayOfYear();

    if (!localStorage.getItem(cachedKey)) {
      saveSpeakersToCache(
        cachedKey,
        prepareSpeakersCache(currentDayOfYear, speakers) as SpeakersOnMainCache
      );
    } else {
      const { dayOfYear }: SpeakersOnMainCache = getSpeakersCache(cachedKey);
      if (dayOfYear !== currentDayOfYear) {
        saveSpeakersToCache(
          cachedKey,
          prepareSpeakersCache(
            currentDayOfYear,
            speakers
          ) as SpeakersOnMainCache
        );
      }
    }

    const { shuffledSpeakers }: SpeakersOnMainCache =
      getSpeakersCache(cachedKey);
    setShuffledSpeakers(shuffledSpeakers);
  }, []);

  const handleCollectionClick = async () => {
    trackAnalyticsData(AnalyticsEvent.CLICK_SPEAKERS_MAIN_PAGE);
  };

  const handleInviteLectorClick = async (e: any) => {
    e.preventDefault();
    trackAnalyticsData(AnalyticsEvent.CLICK_INVITE_LECTOR);
    await router.push('invite-speakers');
  };

  return (
    <div className={css.block}>
      <LayoutConfines>
        <div className={css.header}>
          <LinkArrow
            title="Лекторы"
            href="/speakers"
            testId="Lecturer-main"
            optionalClass="mb-0"
            handleClick={handleCollectionClick}
          />

          <div className={css.buttonsWrapper}>
            <UiButton
              variant="outline"
              href="/invite-speakers"
              className={css.btn}
              onClick={handleInviteLectorClick}
            >
              Стать Лектором
            </UiButton>

            <div
              className={css.prev}
              onClick={() => sliderRef.current?.slidePrev()}
            >
              <UiIcon name="arrow-forward-ios" />
            </div>
            <div
              className={css.next}
              onClick={() => sliderRef.current?.slideNext()}
            >
              <UiIcon name="arrow-forward-ios" />
            </div>
          </div>
        </div>

        <UiSliderNew
          className={css.slider}
          {...C.sliderProps}
          onInit={(slider) => (sliderRef.current = slider)}
        >
          {shuffledSpeakers.map((s, i) => (
            <UiSliderNewSlide className={css.slide} key={i}>
              <Cover
                className={css.slide}
                background={(s.bigPhotoUrl ?? s.photoUrl) || ''}
                imgProxyProps={{
                  sourceWidth: 688,
                  sourceHeight: 800,
                  resizingType: 'fill',
                  gravityType: 'ce',
                }}
                link={
                  s?.status === SpeakerStatusEnum.PreviouslyPerformed
                    ? `/speakers/${s.id}`
                    : undefined
                }
              >
                <span className={css.title}>
                  {`${s.firstName} ${s.lastName}`}
                </span>

                {s.title && (
                  <UiText
                    truncateProps={{ clamp: 3, useNativeClamp: true }}
                    className={css.description}
                  >
                    <p className={css.description}>{s.title || ''}</p>
                  </UiText>
                )}
              </Cover>
            </UiSliderNewSlide>
          ))}
        </UiSliderNew>
      </LayoutConfines>
    </div>
  );
}
